import { Injectable } from '@angular/core';

import { getAppConfig } from 'src/app/utils/utils';
import { environment } from 'src/environments/environment';
import { ListResponse, SlackChannel, SlackTeam, SlackUserGroup } from 'src/models';

import { ApiService } from './api.service';

function formatChannelName(c: SlackChannel) {
  if (c.name == null) {
    return 'None';
  }

  return `${c.name} (${c.isPrivate ? 'private' : 'public'})`;
}

interface SlackChannelListResponse {
  channels: SlackChannel[];
  cursor: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SlackService {
  clientId = environment.slack.clientID;
  appId = environment.slack.appID;

  constructor(private apiService: ApiService) {
    const appConfig = getAppConfig();

    if (appConfig.slackClientId) {
      this.clientId = appConfig.slackClientId;
    }

    if (appConfig.slackAppId) {
      this.appId = appConfig.slackAppId;
    }
  }

  cachedScopes: Record<string, boolean>;
  cachedUserScopes: Record<string, boolean>;
  cachedTeams: { id: string; name: string }[];

  async oauth(fullRedirect: string, origin: string, isSecondary = false, connectionId?: string) {
    const { stateToken, redirectUri } = (await this.apiService.postPromise('/slack/oauth/initiate', {
      redirectUri: fullRedirect,
      isSecondary,
      connectionId,
      includeBotScopes: true,
      includeUserScopes: true,
    })) as any;

    window.open(redirectUri, '_self');
  }

  async userOauth(_fullRedirect: string, origin: string, isSecondary = false, connectionId?: string) {
    const { stateToken, redirectUri } = (await this.apiService.postPromise('/slack/oauth/initiate', {
      redirectUri: `${window.location.origin}/close-window`,
      isSecondary,
      connectionId,
      includeBotScopes: false,
      includeUserScopes: true,
    })) as any;

    window.open(redirectUri, '_blank', 'popup=true');
  }

  async getScopes(connectionId?: string): Promise<{ scopes: Record<string, boolean>; exists: boolean }> {
    const { scopes, exists } = (await this.apiService.getPromise(
      `/slack/scopes${connectionId ? `?connectionId=${connectionId}` : ''}`,
    )) as any;

    this.cachedScopes = scopes;

    return { scopes, exists };
  }

  async getUserScopes(
    connectionId?: string,
  ): Promise<{ scopes: Record<string, boolean>; exists: boolean; slackUserId: string }> {
    const { scopes, exists, slackUserId } = (await this.apiService.getPromise(
      `/me/slack/scopes${connectionId ? `?connectionId=${connectionId}` : ''}`,
    )) as any;

    this.cachedUserScopes = scopes;

    return { scopes, exists, slackUserId };
  }

  async requiresScope(refresh?: boolean, connectionId?: string): Promise<boolean> {
    const scopes = refresh || !this.cachedScopes ? (await this.getScopes(connectionId)).scopes : this.cachedScopes;

    return environment.slack.requiredScopes.some((scope) => !scopes[scope]);
  }

  async requiresUserScope(refresh?: boolean, connectionId?: string): Promise<boolean> {
    const scopes =
      refresh || !this.cachedUserScopes ? (await this.getUserScopes(connectionId)).scopes : this.cachedUserScopes;

    return environment.slack.requiredUserScopes.some((scope) => !scopes[scope]);
  }

  async hasScope(scope: string, refresh?: boolean, connectionId?: string): Promise<boolean> {
    const scopes = refresh || !this.cachedScopes ? (await this.getScopes(connectionId)).scopes : this.cachedScopes;

    return !!scopes[scope];
  }

  async hasUserScope(scope: string, refresh?: boolean, connectionId?: string): Promise<boolean> {
    const scopes =
      refresh || !this.cachedUserScopes ? (await this.getUserScopes(connectionId)).scopes : this.cachedUserScopes;

    return !!scopes[scope];
  }

  async getConnectedChannels(
    includeShared: boolean = true,
    connectionId?: string,
    teamId?: string,
    cursor?: string | null,
    projectId?: string,
  ): Promise<SlackChannelListResponse> {
    const query = [];

    if (includeShared) {
      query.push('includeShared=true');
    }

    if (connectionId) {
      query.push(`connectionId=${connectionId}`);
    }

    if (teamId) {
      query.push(`teamId=${teamId}`);
    }

    if (cursor !== undefined) {
      query.push(`cursor=${encodeURIComponent(cursor)}`);
    }

    if (projectId) {
      query.push(`projectId=${projectId}`);
    }

    const { channels, cursor: channelsCursor }: SlackChannelListResponse = await this.apiService.getPromise(
      `/slack/channels${query.length ? `?${query.join('&')}` : ''}`,
    );

    return {
      channels: Array.isArray(channels)
        ? channels.map((c) => ({
            id: c.id,
            name: c.name,
            displayName: formatChannelName(c),
            isPrivate: c.isPrivate,
            isShared: c.isShared,
            teamId: c.teamId,
          }))
        : [],
      cursor: channelsCursor,
    };
  }

  async getChannel(channelId: string, connectionId?: string, teamId?: string): Promise<SlackChannel | undefined> {
    const query = [];

    if (connectionId) {
      query.push(`connectionId=${connectionId}`);
    }

    if (teamId) {
      query.push(`teamId=${teamId}`);
    }

    const channel: SlackChannel = await this.apiService.getPromise(
      `/slack/channels/${channelId}${query.length ? `?${query.join('&')}` : ''}`,
    );

    if (!channel) {
      return undefined;
    }

    return {
      id: channel.id,
      name: channel.name,
      displayName: formatChannelName(channel),
      isPrivate: channel.isPrivate,
      isShared: channel.isShared,
      teamId: channel.teamId,
      botIds: channel.botIds,
    };
  }

  async getChannelsForUser(
    includeShared: boolean = true,
    connectionId?: string,
    teamId?: string,
    cursor?: string | null,
    projectId?: string,
  ): Promise<SlackChannelListResponse> {
    const query = [];

    if (includeShared) {
      query.push('includeShared=true');
    }

    if (connectionId) {
      query.push(`connectionId=${connectionId}`);
    }

    if (teamId) {
      query.push(`teamId=${teamId}`);
    }

    if (cursor !== undefined) {
      query.push(`cursor=${encodeURIComponent(cursor)}`);
    }

    if (projectId) {
      query.push(`projectId=${projectId}`);
    }

    const { channels, cursor: channelsCursor }: SlackChannelListResponse = await this.apiService.getPromise(
      `/me/slack/channels${query.length ? `?${query.join('&')}` : ''}`,
    );

    return {
      channels: Array.isArray(channels)
        ? channels.map((c) => ({
            id: c.id,
            name: c.name,
            displayName: formatChannelName(c),
            isPrivate: c.isPrivate,
            isShared: c.isShared,
            teamId: c.teamId,
          }))
        : [],
      cursor: channelsCursor,
    };
  }

  async getPublicChannels(
    includeShared: boolean = true,
    connectionId?: string,
    teamId?: string,
    cursor?: string | null,
    projectId?: string,
  ): Promise<SlackChannelListResponse> {
    const query = [];

    if (includeShared) {
      query.push('includeShared=true');
    }

    if (teamId) {
      query.push(`teamId=${teamId}`);
    }

    if (connectionId) {
      query.push(`connectionId=${connectionId}`);
    }

    if (cursor !== undefined) {
      query.push(`cursor=${encodeURIComponent(cursor)}`);
    }

    if (projectId) {
      query.push(`projectId=${projectId}`);
    }

    const { channels, cursor: channelsCursor }: SlackChannelListResponse = await this.apiService.getPromise(
      `/slack/public-channels${query.length ? `?${query.join('&')}` : ''}`,
    );

    return {
      channels: Array.isArray(channels)
        ? channels.map((c) => ({
            id: c.id,
            name: c.name,
            displayName: formatChannelName(c),
            isPrivate: c.isPrivate,
            isShared: c.isShared,
            teamId: c.teamId,
          }))
        : [],
      cursor: channelsCursor,
    };
  }

  async getPrivateChannels(
    includeShared: boolean = true,
    connectionId?: string,
    teamId?: string,
    cursor?: string | null,
  ): Promise<SlackChannelListResponse> {
    const query = [];

    if (includeShared) {
      query.push('includeShared=true');
    }

    if (teamId) {
      query.push(`teamId=${teamId}`);
    }

    if (connectionId) {
      query.push(`connectionId=${connectionId}`);
    }

    if (cursor !== undefined) {
      query.push(`cursor=${encodeURIComponent(cursor)}`);
    }

    const { channels, cursor: channelsCursor }: SlackChannelListResponse = await this.apiService.getPromise(
      `/me/slack/private-channels${query.length ? `?${query.join('&')}` : ''}`,
    );

    return {
      channels: Array.isArray(channels)
        ? channels.map((c) => ({
            id: c.id,
            name: c.name,
            displayName: formatChannelName(c),
            isPrivate: c.isPrivate,
            isShared: c.isShared,
            teamId: c.teamId,
          }))
        : [],
      cursor: channelsCursor,
    };
  }

  async connectChannel(channelId: string, connectionId?: string): Promise<void> {
    await this.apiService.postPromise('/me/slack/connect-channel', { channelId, connectionId });
  }

  async syncChannel(channelId: string): Promise<void> {
    await this.apiService.postPromise(`/slack/channels/${channelId}/sync`, {});
  }

  async getConnectedTeams(reload = false): Promise<any[]> {
    if (!reload && this.cachedTeams) {
      return this.cachedTeams;
    }

    this.cachedTeams = await this.apiService.getPromise('/slack/teams');

    return this.cachedTeams;
  }

  async getSlackConnections(): Promise<any[]> {
    return await this.apiService.getPromise('/slack/connections');
  }

  disconnect(connectionId: string) {
    return this.apiService.postPromise(`/slack/disconnect/${connectionId}`, {});
  }

  disconnectUser() {
    this.apiService.postPromise('/slack/disconnect-user', {});
  }

  async getTeam(teamId: string): Promise<SlackTeam> {
    const result: ListResponse<SlackTeam> = await this.apiService.postPromise('/slack-teams/list', {
      limit: 1,
      order: ['name'],
      descending: false,
      where: [{ field: 'id', operator: '==', value: teamId }],
    });

    return result.data[0];
  }

  createSlackUserGroup(
    name: string,
    description: string,
    handle: string,
    connectionId?: string,
    teamId?: string,
    userGroupId?: string,
  ): Promise<SlackUserGroup> {
    return this.apiService.postPromise('/slack/usergroup', {
      name,
      description,
      handle,
      connectionId,
      teamId,
      userGroupId,
    });
  }

  disableLinkedSlackUserGroup(userGroupId: string): Promise<void> {
    return this.apiService.postPromise(`/slack/usergroup/${userGroupId}/deactivate`, {});
  }

  async getSlackUserGroupBySlackId(slackUserGroupId: string): Promise<SlackUserGroup> {
    return this.apiService.getPromise(`/slack/usergroup/${slackUserGroupId}`);
  }
}
